/**
 * Theme preference for the command centre.
 *
 * Stored under `drtutor.theme` as `light`, `dark` or `system`. The app root
 * applies it, so every surface (auth and public pages included) follows it.
 */

import { useCallback, useEffect, useState } from "react";

import { scopedKey } from "@/lib/tenant/basePath";
export type ThemeMode = "light" | "dark" | "system";

const KEY = scopedKey("drtutor.theme");
const EVENT = "drtutor:theme";

export function readTheme(): ThemeMode {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (raw === "light" || raw === "dark" || raw === "system") return raw;
  } catch {
    /* Storage unavailable: fall back to the default. */
  }
  return "light";
}

export function resolveTheme(mode: ThemeMode): "light" | "dark" {
  if (mode !== "system") return mode;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

/** Browser chrome colour per theme, matching the page canvas. */
const CHROME_COLOR = { light: "#EEF4F3", dark: "#050C0C" } as const;

function applyTheme(mode: ThemeMode): void {
  const resolved = resolveTheme(mode);
  document.documentElement.dataset.theme = resolved;
  // Follows the app's theme, not the device's, so a dark-mode phone still
  // shows a light status bar over the light default.
  // A tenant palette changes the canvas, so read the live token first.
  const live = getComputedStyle(document.documentElement).getPropertyValue("--color-page").trim();
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", live || CHROME_COLOR[resolved]);
}

/** Current preference plus a setter, kept in sync across components. */
export function useTheme(): {
  mode: ThemeMode;
  resolved: "light" | "dark";
  setMode: (m: ThemeMode) => void;
} {
  const [mode, setModeState] = useState<ThemeMode>(() => readTheme());

  useEffect(() => {
    const sync = () => setModeState(readTheme());
    window.addEventListener(EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const setMode = useCallback((m: ThemeMode) => {
    try {
      window.localStorage.setItem(KEY, m);
    } catch {
      /* Storage unavailable: the choice lasts for this page view only. */
    }
    setModeState(m);
    window.dispatchEvent(new CustomEvent(EVENT));
  }, []);

  return { mode, resolved: resolveTheme(mode), setMode };
}

/** Applies the stored preference. Mounted once, at the app root. */
export function useAppliedTheme(): void {
  const { mode } = useTheme();

  useEffect(() => {
    applyTheme(mode);
    if (mode !== "system") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyTheme("system");
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [mode]);
}
